import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { AnomalyRecord, UserPreferences, TimestampVerification } from '../types/bitcoin';

// IndexedDB schema definition
interface BitcoinMonitorDB extends DBSchema {
  anomalies: {
    key: string;
    value: AnomalyRecord;
    indexes: { 
      'by-height': number; 
      'by-date': number;
      'by-severity': string;
    };
  };
  preferences: {
    key: string;
    value: UserPreferences;
  };
  verifications: {
    key: number; // block height
    value: TimestampVerification;
    indexes: { 
      'by-consensus': boolean;
      'by-date': number;
    };
  };
}

export class StorageService {
  private dbPromise: Promise<IDBPDatabase<BitcoinMonitorDB>>;

  constructor() {
    this.dbPromise = this.initDatabase();
  }

  private async initDatabase(): Promise<IDBPDatabase<BitcoinMonitorDB>> {
    return openDB<BitcoinMonitorDB>('bitcoin-monitor', 1, {
      upgrade(db) {
        // Anomalies store
        const anomalyStore = db.createObjectStore('anomalies', { keyPath: 'id' });
        anomalyStore.createIndex('by-height', 'blockHeight');
        anomalyStore.createIndex('by-date', 'detectedAt');
        anomalyStore.createIndex('by-severity', 'verification.anomalies.severity', { multiEntry: true });

        // User preferences store
        db.createObjectStore('preferences', { keyPath: 'id' });

        // Timestamp verifications store
        const verificationStore = db.createObjectStore('verifications', { keyPath: 'blockHeight' });
        verificationStore.createIndex('by-consensus', 'consensus');
        verificationStore.createIndex('by-date', 'blockHeight'); // Using block height as proxy for date
      },
    });
  }

  // Anomaly management
  async saveAnomaly(anomaly: AnomalyRecord): Promise<void> {
    const db = await this.dbPromise;
    await db.put('anomalies', anomaly);
    console.log(`💾 Saved anomaly for block ${anomaly.blockHeight}`);
  }

  async getAnomaly(id: string): Promise<AnomalyRecord | undefined> {
    const db = await this.dbPromise;
    return db.get('anomalies', id);
  }

  async getAnomaliesByHeightRange(startHeight: number, endHeight: number): Promise<AnomalyRecord[]> {
    const db = await this.dbPromise;
    const range = IDBKeyRange.bound(startHeight, endHeight);
    return db.getAllFromIndex('anomalies', 'by-height', range);
  }

  async getAnomaliesByDateRange(startDate: number, endDate: number): Promise<AnomalyRecord[]> {
    const db = await this.dbPromise;
    const range = IDBKeyRange.bound(startDate, endDate);
    return db.getAllFromIndex('anomalies', 'by-date', range);
  }

  async getRecentAnomalies(limit = 50): Promise<AnomalyRecord[]> {
    const db = await this.dbPromise;
    const tx = db.transaction('anomalies', 'readonly');
    const index = tx.store.index('by-date');
    
    // Get anomalies in reverse chronological order
    const anomalies: AnomalyRecord[] = [];
    let cursor = await index.openCursor(null, 'prev');
    let count = 0;
    
    while (cursor && count < limit) {
      anomalies.push(cursor.value);
      cursor = await cursor.continue();
      count++;
    }
    
    return anomalies;
  }

  async getAnomaliesBySeverity(severity: 'minor' | 'major' | 'critical'): Promise<AnomalyRecord[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex('anomalies', 'by-severity', severity);
  }

  async updateAnomalyResolution(id: string, resolved: boolean): Promise<void> {
    const db = await this.dbPromise;
    const anomaly = await db.get('anomalies', id);
    if (anomaly) {
      anomaly.resolved = resolved;
      await db.put('anomalies', anomaly);
    }
  }

  async deleteAnomaly(id: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('anomalies', id);
  }

  // Verification management
  async saveVerification(verification: TimestampVerification): Promise<void> {
    const db = await this.dbPromise;
    await db.put('verifications', verification);
  }

  async getVerification(blockHeight: number): Promise<TimestampVerification | undefined> {
    const db = await this.dbPromise;
    return db.get('verifications', blockHeight);
  }

  async getVerificationsByHeightRange(startHeight: number, endHeight: number): Promise<TimestampVerification[]> {
    const db = await this.dbPromise;
    const range = IDBKeyRange.bound(startHeight, endHeight);
    return db.getAll('verifications', range);
  }

  async getConsensusVerifications(): Promise<TimestampVerification[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex('verifications', 'by-consensus', true);
  }

  async getNonConsensusVerifications(): Promise<TimestampVerification[]> {
    const db = await this.dbPromise;
    return db.getAllFromIndex('verifications', 'by-consensus', false);
  }

  // User preferences management
  async saveUserPreferences(preferences: UserPreferences): Promise<void> {
    const db = await this.dbPromise;
    await db.put('preferences', { id: 'default', ...preferences });
    console.log('💾 User preferences saved');
  }

  async getUserPreferences(): Promise<UserPreferences> {
    const db = await this.dbPromise;
    const stored = await db.get('preferences', 'default');
    
    if (stored) {
      return stored;
    }

    // Return default preferences if none exist
    const defaultPreferences: UserPreferences = {
      alerts: {
        browserNotifications: true,
        anomalyThreshold: 2, // 2 hours
        visualIndicators: true,
      },
      display: {
        refreshInterval: 30, // 30 seconds
        maxHistoryBlocks: 1000,
        chartTimeRange: 24, // 24 hours
      },
      apiPriority: ['mempool', 'blockchair', 'blockchain', 'coinMetrics'],
    };

    await this.saveUserPreferences(defaultPreferences);
    return defaultPreferences;
  }

  // Data export functionality
  async exportAnomalies(): Promise<AnomalyRecord[]> {
    const db = await this.dbPromise;
    return db.getAll('anomalies');
  }

  async exportVerifications(): Promise<TimestampVerification[]> {
    const db = await this.dbPromise;
    return db.getAll('verifications');
  }

  // Cleanup operations
  async cleanupOldData(olderThanDays = 30): Promise<void> {
    const cutoffDate = Date.now() - (olderThanDays * 24 * 60 * 60 * 1000);
    const db = await this.dbPromise;
    
    // Clean old anomalies
    const oldAnomalies = await this.getAnomaliesByDateRange(0, cutoffDate);
    const tx = db.transaction('anomalies', 'readwrite');
    
    for (const anomaly of oldAnomalies) {
      await tx.store.delete(anomaly.id);
    }
    
    console.log(`🧹 Cleaned up ${oldAnomalies.length} old anomaly records`);
  }

  // Statistics
  async getStorageStatistics(): Promise<{
    totalAnomalies: number;
    totalVerifications: number;
    unresolvedAnomalies: number;
    storageSize: number;
  }> {
    const db = await this.dbPromise;
    
    const anomalies = await db.getAll('anomalies');
    const verifications = await db.getAll('verifications');
    const unresolvedAnomalies = anomalies.filter(a => !a.resolved);
    
    // Estimate storage size (rough calculation)
    const storageSize = JSON.stringify([...anomalies, ...verifications]).length;
    
    return {
      totalAnomalies: anomalies.length,
      totalVerifications: verifications.length,
      unresolvedAnomalies: unresolvedAnomalies.length,
      storageSize,
    };
  }

  // Database management
  async clearAllData(): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction(['anomalies', 'verifications'], 'readwrite');
    
    await tx.objectStore('anomalies').clear();
    await tx.objectStore('verifications').clear();
    
    console.log('🗑️ All stored data cleared');
  }

  async close(): Promise<void> {
    const db = await this.dbPromise;
    db.close();
  }
}

// Export singleton instance
export const storage = new StorageService();